import { faSearchMinus, faSearchPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import {
  CustomMarker,
  CustomMarkerChartType,
  CustomMarkerResultType,
  Section,
} from '../../models';
import './import-review.css';

/*
 * The screen a practitioner approves before a PHOTOGRAPHED lab report becomes
 * values on a test.
 *
 * Why this exists at all: a machine-generated Labcorp PDF is parsed from its
 * text layer and is either right or an outright failure. A photo of a foreign
 * lab report is read by a vision model, and it can be quietly, plausibly wrong
 * — in the ten-page Spanish panel this was built against, one row in 111 came
 * back misread. That is a good rate, and it is still not a rate you save to a
 * client's chart unseen.
 *
 * The design follows from two findings in that run, both of which cost real
 * measurement and neither of which is obvious:
 *
 *   1. CONFIDENCE IS WORTHLESS HERE. The model rated every row "high",
 *      including the row it got wrong. So this screen shows no confidence
 *      score. In its place it shows arithmetic identities the panel must
 *      satisfy — direct + indirect = total bilirubin, the differential summing
 *      to 100%, absolutes equalling percent x WBC. A misread digit breaks one.
 *      That is a check on the DATA rather than a self-report from the reader.
 *
 *   2. THE SPANISH LINE IS THE EVIDENCE, so it is the primary text on every
 *      row and the English marker sits under it. What the practitioner is
 *      actually being asked to confirm is the translation and the number, not
 *      the marker name they already know.
 *
 * Bounding boxes were tested and deliberately left out. Asked for a pixel box
 * per row, the model returned boxes that were in bounds, plausibly sized and
 * correctly ordered — and landed on the wrong row: 2 of 15 contained their own
 * value on one page, 0 of 14 on another, drifting up by one to four rows.
 * A highlight that points at the neighbouring row is worse than no highlight,
 * because it invites the practitioner to confirm the wrong evidence. The page
 * viewer zooms and pans instead, and they find the row themselves. Worth
 * re-testing on a newer model: coordinate grounding improved substantially
 * after the generation this was measured on.
 */

export type ImportReviewBucket =
  | 'ready'
  | 'known'
  | 'held'
  | 'custom'
  | 'untracked';

/** A custom marker the read proposes creating, because no shipped field fits. */
export interface ImportReviewCustomMarker {
  name: string;
  /**
   * Whether the result is a number to chart or words to print. Absent means
   * numeric, matching CustomMarker's own default.
   */
  resultType?: 'numeric' | 'text';
  /** The words, when the result is text. "Negativo", "Escasa cantidad", "1 a 2". */
  textValue?: string;
  /** What the lab printed as expected for a text result. */
  expectedText?: string;
  units?: string;
  value?: number;
  precision?: number;
  labMin?: number;
  labMax?: number;
  chartType?: string;
  /** Why labMin/labMax are blank, when they are. */
  rangeNote?: string;
  /** False when the lab printed no usable range, so the row needs one typed. */
  ready?: boolean;
  section?: string;
  sectionLabel?: string;
}

export interface ImportReviewRow {
  idx: number;
  /** "4" for Pag. 4/10. Selects which page photo the viewer shows. */
  page: string;
  spanish: string;
  english: string;
  value: string;
  /** The result in English, when it was words rather than a number. */
  valueEnglish?: string;
  units?: string;
  range?: string;
  markerId?: string;
  markerLabel?: string;
  /** The LabSmarts panel this lands in, for grouping. */
  group?: string;
  bucket: ImportReviewBucket;
  heldReason?: string;
  custom?: ImportReviewCustomMarker;
}

export interface ImportReviewCrossCheck {
  name: string;
  formula: string;
  computed: number;
  printed: number;
  pass: boolean;
}

export interface ImportReviewPayload {
  rows: ImportReviewRow[];
  crossChecks: ImportReviewCrossCheck[];
  /** page number -> image src. Data URI or URL; the component does not care. */
  pages: { [page: string]: string };
  collectionDate?: string;
  fileName?: string;
}

export interface ImportReviewResult {
  /** Shipped marker id -> value, ready to merge into the entry form's state. */
  values: { [markerId: string]: string };
  customMarkers: CustomMarker[];
  collectionDate?: string;
}

interface Props {
  payload: ImportReviewPayload;
  show: boolean;
  onCancel: () => void;
  onImport: (result: ImportReviewResult) => void;
}

/**
 * A text marker needs words; a numeric one needs a lab min before it can chart.
 *
 * The words are judged from the CURRENT field, not the marker's original
 * textValue, because the practitioner edits them in the value column. Reading
 * the original would let a cleared field import as an empty marker, which
 * CustomMarker.isRenderable() then refuses — a row silently lost on save.
 */
const isTextMarker = (c: ImportReviewCustomMarker) => c && c.resultType === 'text';
const customIsImportable = (c: ImportReviewCustomMarker, currentValue?: string) =>
  isTextMarker(c)
    ? !!String(currentValue === undefined ? c.textValue : currentValue || '').trim()
    : !!c && c.labMin != null;

const CHART_TYPES: { [key: string]: CustomMarkerChartType } = {
  standard: CustomMarkerChartType.STANDARD,
  lowerIsBetter: CustomMarkerChartType.LOWER_IS_BETTER,
  higherIsBetter: CustomMarkerChartType.HIGHER_IS_BETTER,
};

/**
 * Markers whose value takes part in one of the arithmetic identities. Shown as
 * a badge on the row, so the practitioner can see WHICH numbers the checks
 * actually cover rather than trusting a single pass/fail tally.
 */
const CROSS_CHECKED = [
  'bilirubin', 'bilirubinDirect', 'bilirubinIndirect', 'protein', 'albumin',
  'globulin', 'albuminGlobulinRatio', 'ldl', 'hdl', 'cholesterol',
  'triglycerides', 'cholesterolHDLRatio', 'whiteBloodCells', 'lymphocytes',
  'lymphocytesAbsolute', 'neutrophils', 'neutrophilsAbsolute', 'monocytes',
  'eosinophils', 'basophils', 'calcium',
];

/**
 * A presigned URL for a PDF, which an <img> cannot render.
 *
 * PDFs come back as one signed URL per page carrying a `#page=N` fragment, so
 * the fragment is the reliable tell; the extension check covers a single-page
 * document that never got one.
 */
const isPdfSource = (src?: string) =>
  !!src && (/#page=/.test(src) || /\.pdf(\?|#|$)/i.test(src));

const num = (raw: string): number => {
  const parsed = parseFloat(String(raw == null ? '' : raw).replace(/,/g, ''));
  return isNaN(parsed) ? undefined : parsed;
};

export const ImportReview: React.FC<Props> = ({
  payload,
  show,
  onCancel,
  onImport,
}) => {
  const rows = payload.rows || [];

  // Keyed by row idx throughout. The read returns rows in page order and the
  // idx is assigned there, so it is stable for the life of one upload.
  /*
   * The field holds what will be SAVED. For a numeric row that is the printed
   * number; for a qualitative one it is the English, because those words end up
   * on a client's report. The Spanish stays on the row and in the source
   * caption, which is what the practitioner checks against the photograph.
   */
  const [values, setValues] = useState<{ [idx: number]: string }>(() => {
    const seed = {};
    rows.forEach(r => {
      seed[r.idx] =
        r.custom && r.custom.resultType === 'text'
          ? r.custom.textValue || r.value
          : r.value;
    });
    return seed;
  });
  const [skipped, setSkipped] = useState<{ [idx: number]: boolean }>({});
  const [customEdits, setCustomEdits] = useState<{
    [idx: number]: Partial<ImportReviewCustomMarker>;
  }>({});
  const [collectionDate, setCollectionDate] = useState(payload.collectionDate || '');
  const [selected, setSelected] = useState<number>(undefined);
  const [showCustom, setShowCustom] = useState(true);
  const [showUntracked, setShowUntracked] = useState(false);

  const ready = useMemo(() => rows.filter(r => r.bucket === 'ready'), [rows]);
  const held = useMemo(() => rows.filter(r => r.bucket === 'held'), [rows]);
  const custom = useMemo(() => rows.filter(r => r.bucket === 'custom'), [rows]);
  const untracked = useMemo(() => rows.filter(r => r.bucket === 'untracked'), [rows]);
  /*
   * Markers the free text parser already imported. Counted so the practitioner
   * can see the read agreed with it, but not listed: this screen exists to show
   * what still needs a decision, and re-importing them would overwrite a good
   * value with a second reading of the same number.
   */
  const known = useMemo(() => rows.filter(r => r.bucket === 'known'), [rows]);

  const groups = useMemo(() => {
    const out: { name: string; rows: ImportReviewRow[] }[] = [];
    ready.forEach(r => {
      const name = r.group || 'Additional Markers';
      let group = out.filter(g => g.name === name)[0];
      if (!group) { group = { name, rows: [] }; out.push(group); }
      group.rows.push(r);
    });
    return out;
  }, [ready]);

  const customFor = (row: ImportReviewRow): ImportReviewCustomMarker => ({
    ...row.custom,
    ...(customEdits[row.idx] || {}),
  });

  const includedReady = ready.filter(r => !skipped[r.idx]).length;
  const includedCustom = custom.filter(
    r => !skipped[r.idx] && customIsImportable(customFor(r), values[r.idx]),
  ).length;

  const passed = (payload.crossChecks || []).filter(c => c.pass).length;

  const handleImport = () => {
    const out: ImportReviewResult = {
      values: {},
      customMarkers: [],
      collectionDate,
    };
    ready.forEach(r => {
      if (skipped[r.idx] || !r.markerId) return;
      out.values[r.markerId] = values[r.idx];
    });
    custom.forEach(r => {
      if (skipped[r.idx]) return;
      const c = customFor(r);
      // Whatever isRenderable() needs, this row has to have: words for a text
      // marker, a lab min for a numeric one. A marker without them saves broken.
      if (!customIsImportable(c, values[r.idx])) return;

      const section = c.section ? (Section[c.section] as Section) : undefined;
      if (isTextMarker(c)) {
        out.customMarkers.push(
          new CustomMarker({
            name: c.name,
            resultType: CustomMarkerResultType.TEXT,
            textValue: values[r.idx],
            expectedText: c.expectedText,
            units: c.units,
            section,
          }),
        );
        return;
      }
      out.customMarkers.push(
        new CustomMarker({
          name: c.name,
          resultType: CustomMarkerResultType.NUMERIC,
          units: c.units,
          value: num(values[r.idx]),
          precision: c.precision,
          labMin: c.labMin,
          labMax: c.labMax,
          chartType: CHART_TYPES[c.chartType] || CustomMarkerChartType.STANDARD,
          section,
        }),
      );
    });
    onImport(out);
  };

  const sourceRow = selected == null ? undefined : rows.filter(r => r.idx === selected)[0];

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size="xl"
      dialogClassName="import-review-dialog"
      backdrop="static"
    >
      <Modal.Header closeButton style={{ backgroundColor: '#337ab7', color: '#fff' }}>
        <Modal.Title as="h5">
          Review imported results
          {payload.fileName ? (
            <span className="import-review-file">{payload.fileName}</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="import-review-body">
        <div className="import-review-grid">
          <div className="import-review-main">
            <div className="import-review-tiles">
              <Tile n={includedReady} k="values ready to import" tone="main" />
              <Tile n={passed} k="arithmetic cross-checks passed" tone="good" />
              {known.length > 0 ? (
                <Tile
                  n={known.length}
                  k="already imported, left alone"
                  tone="hold"
                />
              ) : (
                <Tile n={held.length} k="held back by an import rule" tone="hold" />
              )}
              <Tile n={includedCustom} k="added as your own markers" tone="gap" />
            </div>

            <Form.Group className="import-review-date">
              <Form.Label>Collection date</Form.Label>
              <Form.Control
                type="text"
                value={collectionDate}
                onChange={e =>
                  setCollectionDate((e.target as HTMLInputElement).value)
                }
              />
              <Form.Text>
                Read from “Solicitado el”, which this lab prints day/month/year.
              </Form.Text>
            </Form.Group>

            {groups.map(group => (
              <section className="import-review-card" key={group.name}>
                <header className="import-review-panel-head">
                  <h6>{group.name}</h6>
                  <span>{group.rows.length} values</span>
                </header>
                <Table size="sm" className="import-review-table" responsive>
                  <tbody>
                    {group.rows.map(row => (
                      <ValueRow
                        key={row.idx}
                        row={row}
                        value={values[row.idx]}
                        skipped={!!skipped[row.idx]}
                        selected={selected === row.idx}
                        onSelect={() => setSelected(row.idx)}
                        onToggle={() =>
                          setSkipped({ ...skipped, [row.idx]: !skipped[row.idx] })
                        }
                        onValue={v => setValues({ ...values, [row.idx]: v })}
                      />
                    ))}
                  </tbody>
                </Table>
              </section>
            ))}

            {held.length > 0 && (
              <section className="import-review-card">
                <header className="import-review-head">
                  <h6>Held back by an import rule</h6>
                  <span>{held.length} rows</span>
                </header>
                <p className="import-review-lede">
                  Read correctly and deliberately not imported. Both are values
                  this report prints alongside the one LabSmarts stores.
                </p>
                {held.map(row => (
                  <div className="import-review-held" key={row.idx}>
                    <div className="import-review-es">{row.spanish}</div>
                    <div className="import-review-en">
                      {row.english} = {row.value} {row.units}
                    </div>
                    <div className="import-review-reason">{row.heldReason}</div>
                  </div>
                ))}
              </section>
            )}

            <section className="import-review-card">
              <header className="import-review-head">
                <h6>Added as your own markers</h6>
                <span>{custom.length} rows</span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowCustom(!showCustom)}
                >
                  {showCustom ? 'Hide' : 'Show'}
                </Button>
              </header>
              <p className="import-review-lede">
                The lab reported these and LabSmarts has no field for them, so
                they are created on this test as your own markers, carrying the
                lab's printed range, units and value. Rows where the lab printed
                no usable range need one typed before they can chart.
              </p>
              {showCustom && (
                <Table size="sm" className="import-review-table" responsive>
                  <thead>
                    <tr>
                      <th />
                      <th>Marker</th>
                      <th className="import-review-numcol">Value</th>
                      <th>Units</th>
                      <th className="import-review-numcol">Lab Min</th>
                      <th className="import-review-numcol">Lab Max</th>
                      <th>Section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custom.map(row => (
                      <CustomRow
                        key={row.idx}
                        row={row}
                        custom={customFor(row)}
                        value={values[row.idx]}
                        skipped={!!skipped[row.idx]}
                        selected={selected === row.idx}
                        onSelect={() => setSelected(row.idx)}
                        onToggle={() =>
                          setSkipped({ ...skipped, [row.idx]: !skipped[row.idx] })
                        }
                        onValue={v => setValues({ ...values, [row.idx]: v })}
                        onEdit={patch =>
                          setCustomEdits({
                            ...customEdits,
                            [row.idx]: { ...(customEdits[row.idx] || {}), ...patch },
                          })
                        }
                      />
                    ))}
                  </tbody>
                </Table>
              )}
            </section>

            <section className="import-review-card">
              <header className="import-review-head">
                <h6>Qualitative rows, not imported</h6>
                <span>{untracked.length} rows</span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowUntracked(!showUntracked)}
                >
                  {showUntracked ? 'Hide' : 'Show'}
                </Button>
              </header>
              <p className="import-review-lede">
                Urinalysis, stool studies, Gram stain and morphology comments —
                results whose value is a word, not a number. They wait on
                qualitative custom markers. This is also where the misread risk
                lives: in a block of near-identical words the read can slip a
                row, which is why every numeric value above is either
                cross-checked or numerically distinct.
              </p>
              {showUntracked && (
                <Table size="sm" className="import-review-table" responsive>
                  <tbody>
                    {untracked.map(row => (
                      <tr
                        key={row.idx}
                        className={
                          selected === row.idx ? 'import-review-selected' : undefined
                        }
                        onClick={() => setSelected(row.idx)}
                      >
                        <td>
                          <div className="import-review-es">
                            {row.spanish}
                            <Badge variant="light">p{row.page}</Badge>
                          </div>
                          <div className="import-review-en">{row.english}</div>
                        </td>
                        <td className="import-review-qual">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </section>
          </div>

          <aside className="import-review-side">
            <SourceViewer
              src={sourceRow ? payload.pages[sourceRow.page] : undefined}
              row={sourceRow}
            />
            <div className="import-review-card">
              <header className="import-review-head">
                <h6>Cross-checks</h6>
                <span className="import-review-tally">
                  {passed} / {(payload.crossChecks || []).length} agree
                </span>
              </header>
              <p className="import-review-lede">
                Values on this panel that must agree arithmetically. A misread
                digit breaks one. This replaces a confidence score, which the
                reader rates high even when it is wrong.
              </p>
              {(payload.crossChecks || []).map(check => (
                <div className="import-review-check" key={check.name}>
                  <span
                    className={
                      check.pass ? 'import-review-tick' : 'import-review-cross'
                    }
                  >
                    {check.pass ? '✓' : '✗'}
                  </span>
                  <span>
                    {check.name}
                    <small>{check.formula}</small>
                  </span>
                  <span className="import-review-nums">
                    {check.computed} = {check.printed}
                  </span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </Modal.Body>

      <Modal.Footer className="import-review-footer">
        <Button variant="primary" onClick={handleImport}>
          Import {includedReady + includedCustom} value
          {includedReady + includedCustom === 1 ? '' : 's'}
        </Button>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <span className="import-review-note">
          Values land in the entry form. Nothing is saved until you save the test.
        </span>
      </Modal.Footer>
    </Modal>
  );
};

const Tile: React.FC<{ n: number; k: string; tone: string }> = ({ n, k, tone }) => (
  <div className={`import-review-tile import-review-tile-${tone}`}>
    <span className="import-review-tile-n">{n}</span>
    <span className="import-review-tile-k">{k}</span>
  </div>
);

const ValueRow: React.FC<{
  row: ImportReviewRow;
  value: string;
  skipped: boolean;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onValue: (v: string) => void;
}> = ({ row, value, skipped, selected, onSelect, onToggle, onValue }) => (
  <tr
    className={[
      selected ? 'import-review-selected' : '',
      skipped ? 'import-review-skipped' : '',
    ].join(' ').trim() || undefined}
    onClick={onSelect}
  >
    <td className="import-review-checkcol">
      <Form.Check
        type="checkbox"
        checked={!skipped}
        onChange={onToggle}
        onClick={e => e.stopPropagation()}
        aria-label={`Import ${row.english}`}
      />
    </td>
    <td>
      <div className="import-review-es">
        {row.spanish}
        <Badge variant="light">p{row.page}</Badge>
        {CROSS_CHECKED.indexOf(row.markerId) >= 0 && (
          <Badge variant="success">cross-checked</Badge>
        )}
      </div>
      <div className="import-review-en">
        {row.english}
        {row.markerId ? <code>{row.markerId}</code> : null}
      </div>
    </td>
    <td className="import-review-numcol">
      <Form.Control
        size="sm"
        value={value}
        onClick={e => e.stopPropagation()}
        onChange={e => onValue((e.target as HTMLInputElement).value)}
        className={
          value !== row.value ? 'import-review-edited' : 'import-review-value'
        }
      />
    </td>
    <td className="import-review-units">{row.units}</td>
    <td className="import-review-units">{row.range}</td>
  </tr>
);

const CustomRow: React.FC<{
  row: ImportReviewRow;
  custom: ImportReviewCustomMarker;
  value: string;
  skipped: boolean;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onValue: (v: string) => void;
  onEdit: (patch: Partial<ImportReviewCustomMarker>) => void;
}> = ({ row, custom, value, skipped, selected, onSelect, onToggle, onValue, onEdit }) => {
  const isText = custom.resultType === 'text';
  const missingRange = !isText && custom.labMin == null;
  return (
    <tr
      className={[
        selected ? 'import-review-selected' : '',
        skipped ? 'import-review-skipped' : '',
        missingRange ? 'import-review-needs-range' : '',
      ].join(' ').trim() || undefined}
      onClick={onSelect}
    >
      <td className="import-review-checkcol">
        <Form.Check
          type="checkbox"
          checked={!skipped}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
          aria-label={`Add ${custom.name}`}
        />
      </td>
      <td>
        <div className="import-review-es">
          {row.spanish}
          <Badge variant="light">p{row.page}</Badge>
          {isText && <Badge variant="light">words</Badge>}
        </div>
        <div className="import-review-en">
          {custom.name}
          {isText && row.value && row.value !== value && (
            <span className="import-review-from"> read as “{row.value}”</span>
          )}
        </div>
        {missingRange && (
          <div className="import-review-warn">
            {custom.rangeNote} — type a lab range to chart it.
          </div>
        )}
      </td>
      <td className="import-review-numcol">
        <Form.Control
          size="sm"
          value={value}
          onClick={e => e.stopPropagation()}
          onChange={e => onValue((e.target as HTMLInputElement).value)}
          className={isText ? undefined : 'import-review-value'}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          value={custom.units || ''}
          onClick={e => e.stopPropagation()}
          onChange={e => onEdit({ units: (e.target as HTMLInputElement).value })}
        />
      </td>
      {/*
        * A text result has no range to chart against, so rather than showing two
        * dead number boxes the row says what the lab expected to see. That is
        * the only thing a practitioner can check a word against.
        */}
      {isText ? (
        <td colSpan={2} className="import-review-expected">
          {custom.expectedText ? `lab expected: ${custom.expectedText}` : ''}
        </td>
      ) : (
        <>
          <td className="import-review-numcol">
            <Form.Control
              size="sm"
              value={custom.labMin == null ? '' : String(custom.labMin)}
              placeholder={custom.labMin == null ? 'required' : undefined}
              onClick={e => e.stopPropagation()}
              onChange={e => onEdit({ labMin: num((e.target as HTMLInputElement).value) })}
            />
          </td>
          <td className="import-review-numcol">
            <Form.Control
              size="sm"
              value={custom.labMax == null ? '' : String(custom.labMax)}
              onClick={e => e.stopPropagation()}
              onChange={e => onEdit({ labMax: num((e.target as HTMLInputElement).value) })}
            />
          </td>
        </>
      )}
      <td className="import-review-section">{custom.sectionLabel}</td>
    </tr>
  );
};

/**
 * The page photo, zoomable and pannable.
 *
 * Zoom is applied as a CSS transform on the image rather than by resizing it,
 * so panning never reflows the modal, and the wheel handler keeps the point
 * under the cursor fixed — which is what makes hunting a row on a 960x1280
 * photo bearable at 3x.
 */
const SourceViewer: React.FC<{ src?: string; row?: ImportReviewRow }> = ({
  src,
  row,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  /*
   * A 380px sidebar is enough to see WHICH page a value came from and nowhere
   * near enough to READ it, which makes the panel decorative. Clicking the page
   * opens it over the whole window, where the print is legible and the same
   * zoom and pan still apply.
   */
  const [full, setFull] = useState(false);
  const drag = useRef<{ x: number; y: number; panX: number; panY: number }>(undefined);
  const box = useRef<HTMLDivElement>(null);

  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFull(false); reset(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  const zoomTo = (next: number, originX?: number, originY?: number) => {
    const clamped = Math.min(6, Math.max(1, next));
    const rect = box.current ? box.current.getBoundingClientRect() : undefined;
    const cx = originX == null || !rect ? 0 : originX - rect.left;
    const cy = originY == null || !rect ? 0 : originY - rect.top;
    // Keep the point under the cursor stationary across the scale change.
    setPan(prev => ({
      x: originX == null ? 0 : cx - ((cx - prev.x) * clamped) / zoom,
      y: originY == null ? 0 : cy - ((cy - prev.y) * clamped) / zoom,
    }));
    setZoom(clamped);
  };

  const pdf = isPdfSource(src);

  if (!src) {
    return (
      <div className="import-review-card import-review-viewer">
        <header className="import-review-head">
          <h6>Source</h6>
        </header>
        <p className="import-review-empty">
          Select any row to see the page it was read from.
        </p>
      </div>
    );
  }

  const viewer = (
    <div
      className={
        full
          ? 'import-review-card import-review-viewer import-review-viewer-full'
          : 'import-review-card import-review-viewer'
      }
    >
      <header className="import-review-head">
        <h6>Source</h6>
        <span className="import-review-tally">Pág. {row ? row.page : ''}</span>
        <span className="import-review-zoombtns">
          {!pdf && (
            <>
              <Button
                variant="light"
                size="sm"
                onClick={() => zoomTo(zoom - 0.5)}
                aria-label="Zoom out"
              >
                <FontAwesomeIcon icon={faSearchMinus} />
              </Button>
              <Button variant="light" size="sm" onClick={reset}>
                Fit
              </Button>
              <Button
                variant="light"
                size="sm"
                onClick={() => zoomTo(zoom + 0.5)}
                aria-label="Zoom in"
              >
                <FontAwesomeIcon icon={faSearchPlus} />
              </Button>
            </>
          )}
          <Button
            variant="light"
            size="sm"
            onClick={() => { setFull(!full); reset(); }}
          >
            {full ? 'Close' : 'Full page'}
          </Button>
        </span>
      </header>
      <div
        className={full ? 'import-review-shot import-review-shot-full' : 'import-review-shot'}
        ref={box}
        onWheel={e => {
          e.preventDefault();
          zoomTo(zoom + (e.deltaY < 0 ? 0.35 : -0.35), e.clientX, e.clientY);
        }}
        onMouseDown={e => {
          drag.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
        }}
        onMouseMove={e => {
          if (!drag.current) return;
          setPan({
            x: drag.current.panX + (e.clientX - drag.current.x),
            y: drag.current.panY + (e.clientY - drag.current.y),
          });
        }}
        onMouseUp={() => { drag.current = undefined; }}
        onMouseLeave={() => { drag.current = undefined; }}
        onDoubleClick={e => (zoom > 1 ? reset() : zoomTo(3, e.clientX, e.clientY))}
        onClick={() => { if (!pdf && !full && zoom === 1) setFull(true); }}
        style={{ cursor: pdf ? 'default' : zoom > 1 ? 'grab' : 'zoom-in' }}
      >
        {pdf ? (
          <iframe
            src={src}
            title={`Report page ${row ? row.page : ''}`}
            className="import-review-frame"
          />
        ) : (
          <img
            src={src}
            alt={`Report page ${row ? row.page : ''}`}
            draggable={false}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
            }}
          />
        )}
      </div>
      {row && (
        <p className="import-review-cap">
          Read as <span className="import-review-es">{row.spanish}</span> ={' '}
          <span className="import-review-es">{row.value}</span>
          {row.valueEnglish && row.valueEnglish !== row.value && (
            <>
              {' → '}
              <span className="import-review-es">{row.valueEnglish}</span>
            </>
          )}
          {!full && (
            <span className="import-review-hint">
              {pdf ? ' — open Full page to read it' : ' — click the page to enlarge'}
            </span>
          )}
        </p>
      )}
    </div>
  );

  /*
   * Portalled to the body rather than positioned inside the modal: Bootstrap
   * puts a transform on .modal-dialog while it animates, and a transformed
   * ancestor makes position:fixed resolve against IT rather than the window.
   */
  if (full) {
    return ReactDOM.createPortal(
      <div
        className="import-review-backdrop"
        onClick={e => {
          if (e.target === e.currentTarget) { setFull(false); reset(); }
        }}
      >
        {viewer}
      </div>,
      document.body,
    );
  }
  return viewer;
};
